import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  fetchSyllabusList,
  deleteSyllabusItem,
} from '../api/syllabusApi'
import { getMasterOptionItems } from '../../../admission/students/api/mastersApi'
import { generateAcademicYears } from '../../lectures-list/hooks/useLectureEntryScreen'
import { useExportJob } from '../../../../hooks/useExportJob'
import { appToast } from '../../../../components/AppToast'
import { appConfirm } from '../../../../components/AppConfirmModal'

export const SYLLABUS_SEARCH_FIELDS = [
  { value: 'all', label: 'All Fields' },
  { value: 'course', label: 'Course' },
  { value: 'subject', label: 'Subject' },
  { value: 'chapter', label: 'Chapter' },
  { value: 'topic', label: 'Topic Name' },
]

export const SYLLABUS_PREDEFINED_REPORTS = [
  {
    reportKey: 'syllabus_planned_vs_actual',
    title: 'Syllabus Planned v/s Actual',
    description: 'Compares planned chapter timeline and teaching hours against actual delivered lecture sessions',
    defaultColumns: ['Course', 'Subject', 'Chapter No', 'Topic Name', 'Planned Start', 'Planned End', 'Planned Hours', 'Delivered Hours', 'Variance Status'],
    rowsMatched: '15 rows matched',
  },
  {
    reportKey: 'topic_completion_status',
    title: 'Topic Completion Status',
    description: 'Detailed breakdown of syllabus completion percentage across batches and branches',
    defaultColumns: ['Course', 'Subject', 'Branch', 'Batch', 'Total Chapters', 'Completed', 'In Progress', 'Completion %'],
  },
]

export default function useSyllabusListScreen() {
  const [items, setItems] = useState([])
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [loading, setLoading] = useState(true)

  // Filters
  const [searchField, setSearchField] = useState('all')
  const [searchText, setSearchText] = useState('')
  const [courseFilter, setCourseFilter] = useState('All')
  const [branchFilter, setBranchFilter] = useState('All')
  const [schoolFilter, setSchoolFilter] = useState('All')
  const [academicYearFilter, setAcademicYearFilter] = useState('All')
  const [subjectFilter, setSubjectFilter] = useState('All')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')

  // Universal Background Job for Syllabus Export
  const { triggerExport, isExporting } = useExportJob({ module: 'syllabus' })

  const handleExport = useCallback(
    (format = 'XLSX') => {
      triggerExport(
        {
          academicYear: academicYearFilter !== 'All' ? academicYearFilter : undefined,
          courseId: courseFilter !== 'All' ? Number(courseFilter) : undefined,
          subjectId: subjectFilter !== 'All' ? Number(subjectFilter) : undefined,
          schoolId: schoolFilter !== 'All' ? Number(schoolFilter) : undefined,
          searchText: searchText.trim() ? searchText.trim() : undefined,
          fromDate: fromDate || undefined,
          toDate: toDate || undefined,
        },
        format
      )
    },
    [triggerExport, academicYearFilter, courseFilter, subjectFilter, schoolFilter, searchText, fromDate, toDate]
  )

  // Pagination & Drawer
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [isReportsOpen, setIsReportsOpen] = useState(false)
  const [notesModalItem, setNotesModalItem] = useState(null)

  // Dynamic Masters Options
  const [masterOptions, setMasterOptions] = useState({
    courses: [],
    subjects: [],
    schools: [],
    branches: [],
  })

  // Load Master Options on Mount (DRY from mastersApi)
  useEffect(() => {
    async function loadMasters() {
      try {
        const [cItems, sItems, scItems, brItems] = await Promise.all([
          getMasterOptionItems('courses'),
          getMasterOptionItems('subjects'),
          getMasterOptionItems('schools'),
          getMasterOptionItems('branches'),
        ])
        setMasterOptions({
          courses: Array.isArray(cItems)
            ? cItems.map((c) => ({ value: String(c.id), label: c.name }))
            : [],
          subjects: Array.isArray(sItems)
            ? sItems.map((s) => ({ value: String(s.id), label: s.name }))
            : [],
          schools: Array.isArray(scItems)
            ? scItems.map((sc) => ({ value: String(sc.id), label: sc.name }))
            : [],
          branches: Array.isArray(brItems)
            ? brItems.map((b) => ({ value: String(b.id), label: b.name }))
            : [],
        })
      } catch (err) {
        console.error('Failed to load masters for syllabus filter:', err)
      }
    }
    loadMasters()
  }, [])

  // Server-Side Data Loading
  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const params = {
        page,
        pageSize,
        academicYear: academicYearFilter !== 'All' ? academicYearFilter : undefined,
        courseId: courseFilter !== 'All' ? Number(courseFilter) : undefined,
        subjectId: subjectFilter !== 'All' ? Number(subjectFilter) : undefined,
        schoolId: schoolFilter !== 'All' ? Number(schoolFilter) : undefined,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        searchText: searchText.trim() ? searchText.trim() : undefined,
      }
      const data = await fetchSyllabusList(params)
      const normalizedItems = (data.items || []).map((it) => ({
        ...it,
        course: it.courseName || it.course,
        subject: it.subjectName || it.subject,
        schoolCollege: it.schoolName || (it.schoolType === 2 ? 'All' : 'Single'),
        academicYear: it.aYear || it.academicYear,
        videoUrl: it.video || it.videoUrl,
        hasNotes: Boolean(it.notes),
      }))
      setItems(normalizedItems)
      setTotalCount(data.total || 0)
      setTotalPages(data.totalPages || 1)
    } catch (e) {
      console.error('Failed to load syllabus items:', e)
      appToast.error('Failed to load syllabus items.')
    } finally {
      setLoading(false)
    }
  }, [page, pageSize, academicYearFilter, courseFilter, subjectFilter, schoolFilter, searchText, fromDate, toDate])

  useEffect(() => {
    loadData()
  }, [loadData])

  const resetFilters = () => {
    setSearchField('all')
    setSearchText('')
    setCourseFilter('All')
    setBranchFilter('All')
    setSchoolFilter('All')
    setAcademicYearFilter('All')
    setSubjectFilter('All')
    setFromDate('')
    setToDate('')
    setPage(1)
  }

  const handleDelete = async (id, title) => {
    const ok = await appConfirm({
      title: 'Delete Syllabus Topic',
      message: `Are you sure you want to delete "${title || 'this topic'}"? This action will mark it as deleted.`,
      confirmText: 'Delete',
      variant: 'danger',
    })
    if (!ok) return

    try {
      await deleteSyllabusItem(id)
      appToast.success('Syllabus item deleted successfully.')
      loadData()
    } catch (e) {
      console.error('Error deleting syllabus:', e)
      appToast.error('Failed to delete syllabus item.')
    }
  }

  const handleSaveNotes = async (id, hasNotes, filename) => {
    // Local optimistic update for notes modal
    setItems((prev) =>
      prev.map((it) => (String(it.id) === String(id) ? { ...it, hasNotes, notesFilename: filename } : it))
    )
    appToast.success('Notes updated successfully.')
  }

  return {
    state: {
      items,
      filteredItems: items,
      paginatedItems: items,
      loading,
      searchField,
      searchText,
      courseFilter,
      branchFilter,
      schoolFilter,
      academicYearFilter,
      subjectFilter,
      fromDate,
      toDate,
      isExporting,
      page,
      pageSize,
      totalPages,
      totalCount,
      isReportsOpen,
      notesModalItem,
      options: {
        searchFields: SYLLABUS_SEARCH_FIELDS,
        courses: [{ value: 'All', label: 'All Courses' }, ...masterOptions.courses],
        branches: [{ value: 'All', label: 'All Branches' }, ...masterOptions.branches],
        schools: [{ value: 'All', label: 'All Schools / Colleges' }, ...masterOptions.schools],
        academicYears: [{ value: 'All', label: 'All Academic Years' }, ...generateAcademicYears()],
        subjects: [{ value: 'All', label: 'All Subjects' }, ...masterOptions.subjects],
      },
    },
    actions: {
      setSearchField,
      setSearchText,
      setCourseFilter,
      setBranchFilter,
      setSchoolFilter,
      setAcademicYearFilter,
      setSubjectFilter,
      setFromDate,
      setToDate,
      setPage,
      setPageSize,
      setIsReportsOpen,
      setNotesModalItem,
      resetFilters,
      handleDelete,
      handleSaveNotes,
      handleExport,
      reload: loadData,
    },
  }
}
